/**
 * OneClickPlanButton — Das Herzstück: Monatsplan mit einem Klick.
 *
 * Ruft POST /api/autopilot/one-click auf. Der Endpunkt liest das im Onboarding
 * gespeicherte Markenprofil + Plan-Defaults aus dem Account und erzeugt einen
 * kompletten Monatsplan OHNE jede weitere Eingabe.
 *
 * Props:
 *   variant   — "hero" (große Karte, Default) | "compact" (eine Zeile)
 *   onCreated — optionaler Callback nach Erfolg (z.B. Liste neu laden)
 *   month/year— Zielmonat (Default: laufender Monat). Wird mitgesendet UND für die
 *               Plan-Prüfung benutzt: Existiert für diesen Monat bereits ein Plan,
 *               rendert die Komponente nichts (der fertige Plan gehört nach vorn,
 *               nicht die Aufforderung, ihn zu erzeugen).
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle, ArrowRight } from "lucide-react";
import { trackEvent } from "../lib/trackEvent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = [
  "", "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export default function OneClickPlanButton({
  variant = "hero",
  onCreated,
  month = new Date().getMonth() + 1,
  year  = new Date().getFullYear(),
}) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("idle"); // idle | loading | success
  const [result, setResult] = useState(null);
  // null = Prüfung läuft noch. Solange nichts rendern, sonst blitzt die Hero-Karte
  // kurz auf und verschwindet wieder.
  const [hasPlan, setHasPlan] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Bei Monatswechsel zuerst zurück auf „unbekannt": sonst entschiede kurz der
    // Status des VORIGEN Monats darüber, ob die Karte steht.
    setHasPlan(null);
    axios
      .get(`${API}/monthly-plan/status?month=${month}&year=${year}`)
      .then((res) => { if (!cancelled) setHasPlan(!!res.data?.has_plan); })
      // Fail-safe-Polarität: Bei Fehler wird der Einstieg GEZEIGT. Ein Kunde ohne
      // Plan darf nie ohne Einstiegspunkt dastehen — ein doppelt angebotener Plan
      // ist das kleinere Übel als gar kein Weg zum ersten Plan.
      .catch(() => { if (!cancelled) setHasPlan(false); });
    return () => { cancelled = true; };
  }, [month, year]);

  const generate = async () => {
    setPhase("loading");
    try {
      const res = await axios.post(`${API}/autopilot/one-click`, { month, year });
      setResult(res.data);
      setPhase("success");
      // Ab jetzt existiert für diesen Monat ein Plan. Ohne diese Zeile bliebe der
      // Zustand auf dem Stand des Mount-Zeitpunkts stehen und der Einstiegs-Button
      // käme auf Seiten ohne umgebenden Plan-Zustand (Dashboard) wieder hoch.
      // Die Erfolgsansicht bleibt trotzdem stehen — siehe Render-Guard unten.
      setHasPlan(true);
      const count = res.data?.imported_count ?? res.data?.total_posts ?? 0;
      trackEvent("monthly_plan_generated", { posts_count: count, source: "one_click_button" });
      toast.success(`${count} Posts für ${res.data?.month_name || "diesen Monat"} erstellt 🦊`);
      onCreated?.(res.data);
    } catch (err) {
      setPhase("idle");
      // Nur einen echten String-Detail zeigen (422 liefert Objekt/Array → sonst
      // "[object Object]"); sonst klare Fallback-Meldung.
      const d = err.response?.data?.user_message || err.response?.data?.detail;
      toast.error(
        (typeof d === "string" && d.trim())
          ? d
          : "Der Plan konnte gerade nicht erstellt werden. Bitte versuche es erneut."
      );
    }
  };

  const count = result?.imported_count ?? result?.total_posts ?? 0;

  // Verstecken nur solange nichts erzeugt wurde — die Erfolgsansicht nach dem
  // eigenen Klick bleibt in jedem Fall stehen (sonst verschwände sie sofort,
  // weil der Plan dann ja existiert). Beim nächsten Mount (Reload, zurück aufs
  // Dashboard) steht phase wieder auf "idle" und hasPlan=true blendet den
  // Einstieg korrekt aus.
  if (phase === "idle" && (hasPlan === null || hasPlan === true)) return null;

  // ── Compact-Variante (eine Zeile, z.B. über einer Liste) ────────────────────
  if (variant === "compact") {
    return (
      <button
        onClick={generate}
        disabled={phase === "loading"}
        data-testid="one-click-plan-compact"
        className="inline-flex items-center gap-2 px-4 py-2.5 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm shadow-orange-200"
      >
        {phase === "loading" ? (
          <><Loader2 size={16} className="animate-spin" /> Plane deinen Monat…</>
        ) : (
          <><Sparkles size={16} /> Monatsplan mit 1 Klick</>
        )}
      </button>
    );
  }

  // ── Hero-Variante (große Karte) ─────────────────────────────────────────────
  return (
    <div
      data-testid="one-click-plan-hero"
      className="relative rounded-2xl bg-gradient-to-br from-fox-fire to-orange-400 p-6 text-white shadow-lg shadow-orange-200 overflow-hidden"
    >
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full pointer-events-none" />
      <div className="absolute -bottom-8 -left-4 w-24 h-24 bg-white/10 rounded-full pointer-events-none" />

      <div className="relative">
        {phase !== "success" ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-white/80" />
              <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">
                Dein Monatsplan
              </span>
            </div>
            <h2 className="font-heading text-xl font-bold mb-1">
              Ein ganzer Monat Content — auf Knopfdruck
            </h2>
            <p className="text-sm text-white/85 leading-relaxed mb-4 max-w-md">
              Förderfuchs erstellt deinen kompletten Monatsplan automatisch auf Basis
              deines Markenprofils. Jeden Beitrag kannst du danach prüfen und anpassen.
            </p>
            <button
              onClick={generate}
              disabled={phase === "loading"}
              data-testid="one-click-plan-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white text-fox-fire rounded-xl font-bold text-sm hover:bg-orange-50 disabled:opacity-70 transition-colors shadow-sm"
            >
              {phase === "loading" ? (
                <><Loader2 size={18} className="animate-spin" /> Plane deinen Monat…</>
              ) : (
                <><Sparkles size={18} /> Monatsplan mit 1 Klick erstellen</>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={18} className="text-white" />
              <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
                Fertig!
              </span>
            </div>
            <h2 className="font-heading text-2xl font-bold mb-1">
              {count} Posts erstellt
            </h2>
            <p className="text-sm text-white/85 leading-relaxed mb-4">
              {result?.month_name || MONTH_NAMES[new Date().getMonth() + 1]} ist geplant.
              {result?.bonus_posts_added > 0 && ` (+${result.bonus_posts_added} Bonus-Posts)`}
            </p>
            {/* Nur noch EIN Weg nach vorn: zum Monatsplan.
                Der frühere Knopf „Erneut erstellen" ist bewusst entfallen. Er setzte
                phase zurück auf "idle" — mit hasPlan=true (der Plan existiert ja jetzt)
                würde die Komponente danach nichts mehr rendern, der Knopf führte also
                ins Leere. Fachlich gehört die Neu-Erzeugung ohnehin nicht hierher:
                sie LÖSCHT den gerade erstellten Plan und lebt deshalb als unauffällige
                Sekundäraktion mit Sicherheitsabfrage im PreparedPostsPanel. */}
            <button
              onClick={() => navigate("/monthly-plan")}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white text-fox-fire rounded-xl font-bold text-sm hover:bg-orange-50 transition-colors shadow-sm"
            >
              Zum Monatsplan <ArrowRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
